import { Component, Injector, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ClBaseComponent } from '../cl-base/cl-base.component';
import { Observable, Subscription, take } from 'rxjs';
import { CalDueDateStatus, RadMonitor } from 'src/app/components/catalogs/rad-monitor/rad-monitor/rad-monitor';
import { MatTableDataSource } from '@angular/material/table';
import { FormStatusEnum, WFSaveOptions } from 'src/app/common/enumerations/enumerations';
import moment from 'moment';
import { css } from 'jquery';
import { cloneDeep } from 'lodash';
import { RadLocation } from 'src/app/components/catalogs/rad-monitor/rad-monitor/rad-location';
import { SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'cl-rad-monitor-due-date',
  templateUrl: './cl-rad-monitor-due-date.component.html',
  styleUrls: ['./cl-rad-monitor-due-date.component.scss']
})
export class ClRadMonitorDueDateComponent extends ClBaseComponent implements OnInit, OnDestroy, OnChanges {

  radMonitors?: RadMonitor[];
  radMonitorsList?: RadMonitor[];
  radMonitors$!: Observable<RadMonitor[]>;
  radMonitorsSubs!: Subscription;

  radLocations?: RadLocation[];
  radLocationsList?: RadLocation[];
  radLocationsFiltered?: RadLocation[];
  radLocations$!: Observable<RadLocation[]>;
  radLocationsSubs!: Subscription;

  public dataSource: MatTableDataSource<RadMonitor> = new MatTableDataSource<RadMonitor>();
  public displayedColumns = ['ehs', 'locationType', 'location', 'description', 'dueDate'];

  globalStatus?: CalDueDateStatus;
  showWarning?: boolean;
  mode?: WFSaveOptions;
  override suggested = false;
  questionSafe?: SafeHtml;

  saveEnabled?: boolean;
  valueObject?: any;
  private pendingAutoSave = false;
  private suppressAutoSave = false;
  private forceLiveData = false;

  constructor(
    protected override injector: Injector,
    private zone: NgZone,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadRadLocations();
    this.loadRadMonitors();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.valueObject = this.utils.JSONparse(this.valueString);
    this.checked = this.valueObject?.checked;
    this.mode = this.valueObject?.mode;
    this.user = this.valueObject?.user;
    this.suggested = this.valueObject?.suggested === true;
    this.questionSafe = this.sanitize(this.labelHtml);
    this.loadData();
  }

  override ngOnDestroy(): void {
    this.radMonitorsSubs?.unsubscribe();
    this.radLocationsSubs?.unsubscribe();
  }

  loadRadLocations() {
    this.radLocations$ = this.store.select(state => state.RadLocation.data);
    this.radLocationsSubs = this.radLocations$.subscribe(data => {
      if (data?.length) {
        this.radLocations = data;
        this.loadData();
      }
    });
  }

  loadRadMonitors() {
    this.radMonitors$ = this.store.select(state => state.RadMonitor.data);
    this.radMonitorsSubs = this.radMonitors$.subscribe(data => {
      if (data?.length) {
        this.radMonitors = data;
        this.loadData();
      }
    });
  }

  canModify() {
    return this.editor;
  }

  /**
   * Switches between manual and automatic evaluation, allowing a valid cleared task to be suggested again.
   * @param e Checkbox change event containing the new checked state.
   */
  checkboxChanged(e: any) {
    this.checked = e.checked;
    this.mode = this.checked ? WFSaveOptions.MANUAL : WFSaveOptions.AUTO;
    this.user = this.checked ? this.getCurrentUser(true) : null;
    this.suggested = false;
    if (!this.checked) {
      // Use automatic mode while recalculating so a valid task can regain its recommendation.
      this.valueObject = { ...(this.valueObject ?? {}), mode: WFSaveOptions.AUTO };
      this.suppressAutoSave = true;
      this.forceLiveData = true;
      try {
        this.loadData();
      }
      finally {
        this.suppressAutoSave = false;
        this.forceLiveData = false;
      }
    }
    this.saveDataIfChanged();
  }

  loadData() {
    if (!this.radLocations?.length || !this.radMonitors?.length) {
      return;
    }
    if (this.configuration) {
      this.radMonitorsList = [];
      const radLocationsList = this.utils.JSONparse(this.configuration).data as RadLocation[];
      this.getRadMonitors(radLocationsList);
      // this.radMonitorsList?.sort((a, b) => this.utils.sort(a.ehs, b.ehs));
      this.dataSource = new MatTableDataSource(this.radMonitorsList);
      if (!this.disabled) this.checkStatus();
    }
    else {
      this.radMonitorsList = [];
    }
  }

  getRadMonitors(radLocationsList: RadLocation[]) {
    radLocationsList?.map(l => {
      var radMonitors: RadMonitor[] | undefined = [];
      if (this.editor || this.builder || this.forceLiveData || (!this.disabled && (!this.valueString || this.valueObject?.mode == WFSaveOptions.AUTO || (this.valueObject?.mode == WFSaveOptions.MANUAL && this.checked)))) {

        radMonitors = this.radMonitors?.filter(x => x.radLocationID == l.id);
        this.saveEnabled = true;
        radMonitors?.map(r => {
          r.status = this.getStatus(r.calDueDate, r.status);
          r.cssClass = this.getCssClass(r.status);
          this.radMonitorsList?.push(r);
        });
      }
      else if (this.valueObject) {
        this.checked = this.valueObject.checked;
        this.radMonitorsList = this.valueObject.data;
        this.mode = this.valueObject.mode;
        this.radMonitorsList?.map(r => {
          r.radLocation = this.radLocations?.find(l => l.id == r.radLocationID);
        });
        this.user = this.valueObject.user;
        this.suggested = this.valueObject.suggested === true;
      }
    });
  }

  getStatus(date?: Date | null, currentStatus?: CalDueDateStatus) {
    if (this.builder || this.editor || (!this.builder && !this.disabled)) {
      let status = CalDueDateStatus.Good;
      if (moment(date).isBefore(moment(new Date)))
        status = CalDueDateStatus.Expired;
      else if (moment(date).isBefore(moment(new Date).add(14, 'days')))
        status = CalDueDateStatus.CloseToExpire;
      return status;
    }
    else return currentStatus;
  }

  getCssClass(status?: CalDueDateStatus) {
    switch (status) {
      case CalDueDateStatus.Expired:
        return 'red';
      case CalDueDateStatus.CloseToExpire:
        return 'yellow';
      default:
        return 'green';
    }
  }

  /**
   * Evaluates calibration status and persists a recommendation without creating a signature.
   */
  checkStatus() {
    const checked = this.checked;
    const suggested = this.suggested;
    const automatic = this.valueObject?.mode !== WFSaveOptions.MANUAL;
    if (this.radMonitorsList?.some(r => r.status == CalDueDateStatus.Expired)) {
      this.checked = false;
      this.user = null;
      this.suggested = false;
      this.showWarning = true;
      this.globalStatus = CalDueDateStatus.Expired;
    }
    else if (this.radMonitorsList?.some(r => r.status == CalDueDateStatus.CloseToExpire)) {
      if (!this.checked || this.user?.id == 1) {
        this.checked = false;
        this.user = null;
      }
      this.suggested = false;
      this.showWarning = true;
      this.globalStatus = CalDueDateStatus.CloseToExpire;
    }
    else {
      if (automatic) {
        this.checked = false;
        this.user = null;
        this.suggested = true;
      }
      else
        this.suggested = false;
      this.showWarning = false;
      this.globalStatus = CalDueDateStatus.Good;
    }
    if ((this.checked != checked || this.suggested != suggested) && !this.builder && !this.editor && this.saveEnabled) {
      this.mode = WFSaveOptions.AUTO;
      if (!this.suppressAutoSave) {
        this.autoSaveData();
      }
      this.saveEnabled = false;
    }
  }

  autoSaveData() {
    if (this.pendingAutoSave) return;
    this.pendingAutoSave = true;
    this.zone.onStable.pipe(take(1)).subscribe(() => {
      this.pendingAutoSave = false;
      this.saveDataIfChanged();
    });
  }

  saveDataIfChanged() {
    if (this.valueObject?.checked !== this.checked ||
      this.valueObject?.mode !== this.mode ||
      this.valueObject?.suggested !== this.suggested ||
      (this.valueObject?.user?.id ?? null) !== (this.user?.id ?? null)) {
      this.saveData();
    }
  }

  saveData() {
    const radMonitorsList: any[] = [];
    this.radMonitorsList?.map(r => {
      radMonitorsList.push(this.utils.Serialize(r));
    });
    const values = this.utils.JSONstringify({
      data: radMonitorsList,
      checked: this.checked,
      user: this.user,
      suggested: this.suggested,
      suggestionReason: this.suggested ? 'All selected monitors are within their calibration dates.' : null,
      mode: this.mode
    });
    this.valueString = values;
    this.valueObject = this.utils.JSONparse(values);
    this.changed.emit(values);
  }
}
